// src/components/SimpleComponents/SimpleSelectAsyncProducts.jsx
// *******************************************************************************
// ASYNC SELECT FOR PRODUCTS
// *******************************************************************************
// Async select component for searching and selecting products from the database.
// Uses debounced search and displays product info (SKU, description, price).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Select, Spin, Flex, Typography, Tag, Empty, Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import axiosService from 'services/axiosService';
import financialService from 'services/financialService';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { API } from 'constants/Api';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 20;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleSelectAsyncProducts = ({
  name,
  subdocumentField,
  style = {},
  styleItem = {},
  disabled = false,
  placeholder = 'search_product',
  onChange,
  onProductSelect,
  onBlur,
  validateTrigger = 'onBlur',
  showFeedback = false,
  required: requiredProp,
  showPrice = true,
  showSku = true,
  minSearchLength = MIN_SEARCH_LENGTH,
  debounceDelay = DEBOUNCE_DELAY,
  maxResults = MAX_RESULTS,
  selectedProduct = null, // Product object to display when value is set externally
  environment = 'sale', // 'sale' (default) | 'purchase' — changes dropdown layout
  issuerId = null, // Entity _id of the supplier (used when environment='purchase' to find matching supplier ref)
  ...props
}) => {
  // Resolve unitUid to unit name using product parameters (for purchase mode)
  const storedParameters = useZustand('app', 'parameters');
  const resolveUnitName = useCallback(
    unitUid => {
      if (!unitUid) return '';
      const units = storedParameters?.modules?.products?.units;
      if (!Array.isArray(units)) return '';
      const found = units.find(u => u.uid === unitUid);
      return found?.name || '';
    },
    [storedParameters]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, shouldShowErrors, requiredFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const debounceTimerRef = useRef(null);
  const lastSearchRef = useRef('');

  // Watch field value for validation
  const fieldValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const required = requiredProp ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [customValidationMessage, setCustomValidationMessage] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Create option from selectedProduct when value is set externally (e.g., after editing)
  useEffect(() => {
    if (!selectedProduct?._id) return;

    // Create option for the selected product and set it
    // This ensures the Select shows the product name instead of the _id
    const productOption = {
      value: selectedProduct._id,
      label: getProductLabel(selectedProduct, { environment, resolveUnitName }),
      product: {
        ...selectedProduct,
        __richLabel: buildProductLabel(selectedProduct, {
          showPrice,
          showSku,
          environment,
          issuerId,
          resolveUnitName,
        }),
      },
    };

    setOptions(prevOptions => {
      // Check if this product is already in options
      const existingOption = prevOptions.find(opt => opt.value === selectedProduct._id);
      if (existingOption) return prevOptions;

      // Add the new option (replace all options since this is external selection)
      return [productOption];
    });
  }, [selectedProduct, showPrice, showSku, environment, issuerId, resolveUnitName]);

  // Activate validation when form shows errors
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Validate field value
  useEffect(() => {
    if (!shouldValidate) return;

    const hasValue = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

    if (!hasValue && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (hasValue) {
      setCustomValidationStatus('success');
      setCustomValidationMessage('');
      return;
    }

    setCustomValidationStatus('');
    setCustomValidationMessage('');
  }, [fieldValue, required, shouldValidate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(
    async searchValue => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Skip if search is too short
      if (!searchValue || searchValue.length < minSearchLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      // Skip if same search
      const normalizedSearch = searchValue.toLowerCase().trim();
      if (normalizedSearch === lastSearchRef.current) {
        return;
      }

      setLoading(true);

      // Debounce the actual search
      debounceTimerRef.current = setTimeout(async () => {
        try {
          lastSearchRef.current = normalizedSearch;

          // Use power-search endpoint (POST with body) for faster indexed search
          // returnAsObject: false to get the full array of results
          const [products] = await axiosService.post({
            url: API.PRODUCTS.POWER_SEARCH,
            data: { search: searchValue },
            returnAsObject: false,
          });

          // Limit results after response
          const limitedProducts = Array.isArray(products) ? products.slice(0, maxResults) : [];

          // Transform products to select options
          // Note: Only use standard Ant Design props (value, label) to avoid DOM warnings
          // Store rich label in product object for optionRender to access
          const productOptions = limitedProducts.map(product => ({
            value: product._id,
            label: getProductLabel(product, { environment, resolveUnitName }), // Simple text for selected display
            product: {
              ...product,
              __richLabel: buildProductLabel(product, {
                showPrice,
                showSku,
                environment,
                issuerId,
                resolveUnitName,
              }), // Rich JSX for dropdown
            },
          }));

          setOptions(productOptions);
        } catch (error) {
          console.error('[SimpleSelectAsyncProducts] Search error:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceDelay);
    },
    [
      minSearchLength,
      maxResults,
      debounceDelay,
      showPrice,
      showSku,
      environment,
      issuerId,
      resolveUnitName,
    ]
  );

  const handleSelect = useCallback(
    (value, option) => {
      setOpen(false);
      setShouldValidate(true);

      // Call onChange with just the ID
      if (typeof onChange === 'function') {
        onChange(value, option);
      }

      // Call onProductSelect with full product data
      if (typeof onProductSelect === 'function' && option?.product) {
        onProductSelect(option.product);
      }
    },
    [onChange, onProductSelect]
  );

  const handleDropdownVisibleChange = useCallback(visible => {
    setOpen(visible);
    if (!visible) {
      // Clear search state when closing
      lastSearchRef.current = '';
    }
  }, []);

  const handleBlur = useCallback(
    ev => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (typeof onBlur === 'function') {
        setTimeout(() => onBlur(ev), 0);
      }
    },
    [onBlur]
  );

  const handleClear = useCallback(() => {
    setOptions([]);
    lastSearchRef.current = '';
    setShouldValidate(true);

    if (typeof onChange === 'function') {
      onChange(null, null);
    }
    if (typeof onProductSelect === 'function') {
      onProductSelect(null);
    }
  }, [onChange, onProductSelect]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <Flex justify="center" style={{ padding: 16 }}>
          <Spin size="small" />
        </Flex>
      );
    }

    if (lastSearchRef.current && options.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('no_products_found')}
          style={{ margin: '16px 0' }}
        />
      );
    }

    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Text type="secondary">{translateX('type_to_search_products')}</Text>
      </Flex>
    );
  }, [loading, options.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      style={styleItem}
      validateTrigger={validateTrigger}
      showFeedback={showFeedback}
      required={required}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Select
        ref={elementRef}
        id={flattenFieldName}
        style={{ height: 40, ...style }}
        showSearch
        allowClear
        disabled={isDisabled}
        placeholder={translateX(placeholder)}
        filterOption={false} // Disable client-side filtering, we use server-side
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        onBlur={handleBlur}
        open={open}
        onOpenChange={handleDropdownVisibleChange}
        notFoundContent={notFoundContent}
        loading={loading}
        suffixIcon={loading ? <Spin size="small" /> : <SearchOutlined />}
        options={options}
        optionRender={option => option.data.product?.__richLabel}
        labelInValue={false}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleSelectAsyncProducts;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Get product label as a simple string for display in Select
 * Handles object format fields (Brazilian fiscal: {comercial, tributavel})
 * @param {Object} product - Product data
 * @returns {string} Simple string label
 */
function getProductLabel(product, { environment, resolveUnitName } = {}) {
  const { name, description, sku, price, unitUid, unit } = product || {};

  // Handle name field (might be object with {comercial, tributavel})
  const nameValue = resolveObjectField(name) || resolveObjectField(description) || '-';

  // Purchase mode: "SKU - NAME ··· UNIT | PRICE" with space-between
  if (environment === 'purchase') {
    const parts = [];
    if (sku) parts.push(sku);
    parts.push(nameValue);
    const left = parts.join(' - ');

    const unitName = resolveUnitName?.(unitUid) || (typeof unit === 'string' ? unit : '') || '';
    const priceValue = resolveObjectField(price, true) || 0;
    const rightParts = [];
    if (unitName) rightParts.push(unitName);
    if (priceValue > 0) rightParts.push(financialService.formatCurrency(priceValue));
    const right = rightParts.join(' | ');

    if (!right) return left;

    return (
      <span
        style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
        className="text-uppercase"
      >
        <span>{left}</span>
        <span style={{ marginRight: 16 }}>{right}</span>
      </span>
    );
  }

  return nameValue;
}

/**
 * Build product label for select option.
 *
 * environment='sale' (default):
 *   Line 1 (bold): NAME
 *   Line 2 (secondary): SKU | LC | EAN | PRICE   [UNIT] [BRAND]
 *
 * environment='purchase':
 *   Line 1 (bold): SKU | NAME  ···  UNIT | PRICE  (sale data — our catalog)
 *   Line 2 (secondary): SKU | DESCRIPTION  ···  UNIT | PRICE  (purchase data — from supplier)
 *
 * @param {Object} product - Product data (includes suppliers[] array)
 * @param {Object} options - Display options
 * @returns {JSX.Element} Formatted label
 */
function buildProductLabel(
  product,
  { showPrice = true, showSku = true, environment = 'sale', issuerId = null, resolveUnitName } = {}
) {
  const { name, sku, ean, price, unit, unitUid, brand, legacyCode, suppliers } = product;

  // Resolve object-format fields
  const displayName = resolveObjectField(name) || '-';
  const priceValue = resolveObjectField(price, true) || 0;

  // Resolve unit name: prefer unitUid lookup from parameters, fallback to unit field
  const unitFromUid = resolveUnitName?.(unitUid) || '';
  const unitFromField =
    typeof unit === 'object' && unit !== null
      ? (unit.name ?? unit.comercial ?? unit.tributavel ?? '')
      : (unit ?? '');
  const unitValue = unitFromUid || unitFromField;

  // ── Sale mode (default) ──────────────────────────────────────────────────
  if (environment !== 'purchase') {
    const secondaryParts = [];
    if (showSku && sku) secondaryParts.push(`SKU: ${sku}`);
    if (legacyCode?.length > 0) {
      const lcValue = Array.isArray(legacyCode) ? legacyCode.join(',') : legacyCode;
      secondaryParts.push(`LC: ${lcValue}`);
    }
    if (ean) secondaryParts.push(`EAN: ${ean}`);
    if (showPrice && priceValue > 0)
      secondaryParts.push(financialService.formatCurrency(priceValue));
    const secondaryLine = secondaryParts.join(' | ');

    return (
      <Flex justify="space-between" align="center" style={{ width: '100%', padding: '4px 0' }}>
        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
          <Text ellipsis strong style={{ lineHeight: 1.3, textTransform: 'uppercase' }}>
            {displayName}
          </Text>
          {secondaryLine && (
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
              {secondaryLine}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap={4} style={{ marginLeft: 12 }}>
          {unitValue && (
            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
              {unitValue}
            </Tag>
          )}
          {brand && (
            <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
              {brand}
            </Tag>
          )}
        </Flex>
      </Flex>
    );
  }

  // ── Purchase mode ────────────────────────────────────────────────────────
  // Find the matching supplier reference for this issuer
  const supplierRef = findSupplierRef(suppliers, issuerId);

  // Line 1 (bold): Sale info — our catalog data
  const saleParts = [];
  if (sku) saleParts.push(sku);
  saleParts.push(displayName);
  const saleLeft = saleParts.join(' | ');

  const saleRightParts = [];
  if (unitValue) saleRightParts.push(unitValue);
  if (priceValue > 0) saleRightParts.push(financialService.formatCurrency(priceValue));
  const saleRight = saleRightParts.join(' | ');

  // Line 2 (secondary): Purchase info — supplier data
  const purchaseLeft = supplierRef
    ? [supplierRef.sku, supplierRef.description].filter(Boolean).join(' | ') || '—'
    : '—';

  const purchaseRightParts = [];
  const supplierUnitName = resolveUnitName?.(supplierRef?.unitUid) || supplierRef?.unit || '';
  if (supplierUnitName) purchaseRightParts.push(supplierUnitName);
  if (supplierRef?.purchasePrice > 0)
    purchaseRightParts.push(financialService.formatCurrency(supplierRef.purchasePrice));
  const purchaseRight = purchaseRightParts.join(' | ');

  return (
    <Flex vertical style={{ width: '100%', padding: '4px 0' }}>
      {/* Line 1: Sale data (bold) */}
      <Flex justify="space-between" align="center">
        <Text
          ellipsis
          strong
          style={{ lineHeight: 1.3, textTransform: 'uppercase', flex: 1, minWidth: 0 }}
        >
          {saleLeft}
        </Text>
        {saleRight && (
          <Text
            strong
            style={{ fontSize: 12, lineHeight: 1.3, marginLeft: 12, whiteSpace: 'nowrap' }}
          >
            {saleRight}
          </Text>
        )}
      </Flex>
      {/* Line 2: Purchase data (secondary) */}
      <Flex justify="space-between" align="center">
        <Text
          ellipsis
          type="secondary"
          style={{ fontSize: 12, lineHeight: 1.3, flex: 1, minWidth: 0 }}
        >
          {purchaseLeft}
        </Text>
        {purchaseRight && (
          <Text
            type="secondary"
            style={{ fontSize: 12, lineHeight: 1.3, marginLeft: 12, whiteSpace: 'nowrap' }}
          >
            {purchaseRight}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

/**
 * Resolve a field that may be an object (Brazilian fiscal: {comercial, tributavel}) or a plain value.
 */
function resolveObjectField(value, isNumeric = false) {
  if (typeof value === 'object' && value !== null) {
    return value.comercial ?? value.tributavel ?? (isNumeric ? 0 : '');
  }
  return value ?? (isNumeric ? 0 : '');
}

/**
 * Find matching supplier reference by issuerId (supplierId).
 * Falls back to the primary supplier if no issuerId match.
 */
function findSupplierRef(suppliers, issuerId) {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return null;

  // Try exact match on issuerId
  if (issuerId) {
    const match = suppliers.find(
      s => s.isActive !== false && String(s.supplierId) === String(issuerId)
    );
    if (match) return match;
  }

  // Fallback: primary supplier
  return suppliers.find(s => s.isPrimary && s.isActive !== false) || null;
}
